import { useEffect, useMemo, useRef, useState } from "react";
import { Bell, X, CheckCheck, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

const TYPE_STYLES = {
  register: { color: "text-sky-400", bg: "bg-sky-500/10", label: "Cadastro" },
  deposit_created: { color: "text-amber-400", bg: "bg-amber-500/10", label: "Depósito" },
  deposit_approved: { color: "text-emerald-400", bg: "bg-emerald-500/10", label: "Depósito OK" },
  withdraw_requested: { color: "text-orange-400", bg: "bg-orange-500/10", label: "Saque" },
  withdraw_paid: { color: "text-emerald-400", bg: "bg-emerald-500/10", label: "Saque enviado" },
  funnel_sale: { color: "text-[var(--accent)]", bg: "bg-yellow-500/10", label: "Venda" },
  subscription_created: { color: "text-sky-400", bg: "bg-sky-500/10", label: "Assinatura" },
  subscription_overdue: { color: "text-red-400", bg: "bg-red-500/10", label: "Vencida" },
  payment_rejected: { color: "text-red-400", bg: "bg-red-500/10", label: "Falha" },
  _default: { color: "text-slate-300", bg: "bg-slate-500/10", label: "Aviso" },
};

function timeAgo(iso) {
  try {
    const d = new Date(iso);
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 60) return `há ${sec}s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `há ${min}min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `há ${h}h`;
    const day = Math.floor(h / 24);
    if (day < 7) return `há ${day}d`;
    return d.toLocaleDateString("pt-BR");
  } catch (_) {
    return "";
  }
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [filterType, setFilterType] = useState("");
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const loadCount = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.unread || 0);
    } catch (_) {
      // ignora se não estiver logado
    }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: "1", per_page: "50" });
      if (filterType) params.set("type", filterType);
      const { data } = await api.get(`/notifications?${params.toString()}`);
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCount();
    pollRef.current = setInterval(loadCount, 25000); // poll leve
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    if (open) loadItems();
  }, [open, filterType]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.toLowerCase();
    return items.filter(
      (i) =>
        (i.title || "").toLowerCase().includes(needle) ||
        (i.body || "").toLowerCase().includes(needle)
    );
  }, [items, q]);

  const markRead = async (id) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, read: true } : i)));
    setUnread((n) => Math.max(0, n - 1));
    try {
      await api.post(`/notifications/${id}/read`);
    } catch (_) { /* swallow */ }
  };

  const markAllRead = async () => {
    setBusy(true);
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
      setUnread(0);
      toast.success("Todas marcadas como lidas");
    } catch (_) {
      toast.error("Falha ao marcar todas");
    } finally {
      setBusy(false);
    }
  };

  const deleteOne = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (_) {
      toast.error("Falha ao excluir");
    }
  };

  const deleteAll = async () => {
    if (!confirm("Excluir TODAS as notificações? Essa ação não pode ser desfeita.")) return;
    setBusy(true);
    try {
      await api.delete("/notifications");
      setItems([]);
      setUnread(0);
      toast.success("Todas excluídas");
    } catch (_) {
      toast.error("Falha ao excluir todas");
    } finally {
      setBusy(false);
    }
  };

  const uniqueTypes = useMemo(() => {
    const s = new Set(items.map((i) => i.type));
    return Array.from(s);
  }, [items]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed top-4 right-4 md:top-6 md:right-6 z-30 w-11 h-11 rounded-full bg-[var(--bg-elev)]/90 backdrop-blur-md border border-white/10 flex items-center justify-center hover:border-[var(--accent)] transition-colors shadow-lg"
        data-testid="notification-bell-btn"
        aria-label="Notificações"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[20px] h-[20px] rounded-full bg-[var(--accent)] text-black text-[10px] font-bold flex items-center justify-center px-1"
            data-testid="notification-bell-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <aside
            className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[420px] bg-[var(--bg-base)] border-l border-[var(--border)] flex flex-col"
            data-testid="notification-drawer"
          >
            <header className="p-4 border-b border-[var(--border)] flex items-center justify-between">
              <div>
                <div className="text-xs opacity-60 uppercase tracking-wider">Caixa de Mensagens</div>
                <div className="text-lg font-bold flex items-center gap-2">
                  Notificações
                  {unread > 0 && (
                    <span className="text-xs bg-[var(--accent)] text-black px-2 py-0.5 rounded-sm">
                      {unread} nova{unread > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-sm border border-[var(--border)] flex items-center justify-center hover:bg-[var(--bg-elev)]"
                data-testid="notification-close"
              >
                <X className="w-4 h-4" />
              </button>
            </header>

            <div className="p-3 border-b border-[var(--border)] space-y-2">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Pesquisar..."
                className="w-full bg-[var(--bg-elev)] border border-[var(--border)] rounded-sm px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
                data-testid="notification-search"
              />
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setFilterType("")}
                  className={`text-[10px] px-2 py-1 rounded-sm border ${
                    filterType === "" ? "border-[var(--accent)] text-[var(--accent)]" : "border-[var(--border)] opacity-70"
                  }`}
                >
                  Todos
                </button>
                {uniqueTypes.map((t) => {
                  const s = TYPE_STYLES[t] || TYPE_STYLES._default;
                  return (
                    <button
                      key={t}
                      onClick={() => setFilterType(t)}
                      className={`text-[10px] px-2 py-1 rounded-sm border ${
                        filterType === t ? "border-[var(--accent)] text-[var(--accent)]" : "border-[var(--border)] opacity-70"
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  disabled={busy || unread === 0}
                  onClick={markAllRead}
                  className="flex-1 text-xs border border-[var(--border)] rounded-sm py-1.5 flex items-center justify-center gap-1 hover:bg-[var(--bg-elev)] disabled:opacity-50"
                  data-testid="notification-mark-all-read"
                >
                  <CheckCheck className="w-3 h-3" /> Marcar todas
                </button>
                <button
                  disabled={busy || items.length === 0}
                  onClick={deleteAll}
                  className="flex-1 text-xs border border-red-500/40 text-red-400 rounded-sm py-1.5 flex items-center justify-center gap-1 hover:bg-red-500/10 disabled:opacity-50"
                  data-testid="notification-delete-all"
                >
                  <Trash2 className="w-3 h-3" /> Excluir todas
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto" data-testid="notification-list">
              {loading && (
                <div className="p-6 flex items-center justify-center opacity-60">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              )}
              {!loading && filtered.length === 0 && (
                <div className="p-6 text-center opacity-50 text-sm">Nenhuma notificação por aqui.</div>
              )}
              {!loading &&
                filtered.map((n) => {
                  const s = TYPE_STYLES[n.type] || TYPE_STYLES._default;
                  return (
                    <div
                      key={n.id}
                      onClick={() => !n.read && markRead(n.id)}
                      className={`group px-4 py-3 border-b border-[var(--border)] cursor-pointer hover:bg-[var(--bg-elev)] transition-colors ${
                        !n.read ? "bg-[var(--bg-elev)]/40" : ""
                      }`}
                      data-testid={`notification-item-${n.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={`shrink-0 mt-1 text-[10px] px-1.5 py-0.5 rounded-sm border border-[var(--border)] ${s.color} ${s.bg}`}
                        >
                          {s.label}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-semibold truncate ${!n.read ? "text-white" : "text-white/70"}`}>
                              {n.title}
                            </span>
                            {!n.read && <span className="w-2 h-2 rounded-full bg-[var(--accent)] shrink-0" />}
                          </div>
                          {n.body && <p className="text-xs opacity-70 mt-0.5 break-words">{n.body}</p>}
                          <div className="flex items-center gap-3 mt-2 text-[10px] opacity-50">
                            <span>{timeAgo(n.created_at)}</span>
                            {n.link && (
                              <Link
                                to={n.link}
                                onClick={() => setOpen(false)}
                                className="flex items-center gap-1 hover:text-[var(--accent)]"
                                data-testid={`notification-link-${n.id}`}
                              >
                                <ExternalLink className="w-3 h-3" /> Abrir
                              </Link>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteOne(n.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-300"
                          data-testid={`notification-delete-${n.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </aside>
        </>
      )}
    </>
  );
}
